import { z } from "zod";

export const loginRequestSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
  tenantId: z.number().int().optional(),
});

export const mfaVerificationRequestSchema = z.object({
  session: z.string(),
  mfaCode: z.string().length(6, "MFA code must be 6 digits"),
  username: z.string(),
  challengeName: z.enum(["SMS_MFA", "SOFTWARE_TOKEN_MFA"]),
  tenantId: z.number().int().optional(),
});

export const resetPasswordRequestSchema = z.object({
  username: z.string(),
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
  session: z.string(),
  tenantId: z.number().int().optional(),
});

export const resetPasswordResponseSchema = z.object({
  accessToken: z.string().nullable(),
  refreshToken: z.string().nullable(),
  challengeName: z.string().nullable(),
  session: z.string().nullable(),
  success: z.boolean(),
  message: z.string().nullable(),
});

export const setupMfaRequestSchema = z.object({
  accessToken: z.string().optional(),
  session: z.string().optional(),
  username: z.string().optional(),
  mfaType: z.enum(["TOTP", "SMS"]),
  phoneNumber: z.string().optional(),
});

export const setupMfaResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().nullable(),
  secretCode: z.string().optional(),
  qrCodeUrl: z.string().optional(),
  session: z.string().nullable(),
});

export const confirmMfaRequestSchema = z.object({
  session: z.string().optional(),
  username: z.string().optional(),
  mfaCode: z.string().length(6, "MFA code must be 6 digits"),
  mfaType: z.enum(["TOTP", "SMS"]),
});

export const confirmMfaResponseSchema = z.object({
  accessToken: z.string().nullable(),
  refreshToken: z.string().nullable(),
  challengeName: z.string().nullable(),
  session: z.string().nullable(),
  success: z.boolean(),
  message: z.string().nullable(),
});

const tenantDetailsSchema = z.object({
  tenantId: z.number().int(),
  name: z.string(),
  region: z.string().optional(),
});

export const loginResponseSchema = z.object({
  accessToken: z.string().nullable(),
  refreshToken: z.string().nullable(),
  challengeName: z.string().nullable(),
  session: z.string().nullable(),
  success: z.boolean(),
  message: z.string().nullable(),
  tenants: z.array(tenantDetailsSchema).nullable().optional(),
  selectedTenantId: z.union([z.string(), z.number()]).optional(),
});

export const refreshTokenRequestSchema = z.object({
  refreshToken: z.string(),
  username: z.string(),
  tenantId: z.number().int().optional(),
});

export const refreshTokenResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;
export type LoginResponse = z.infer<typeof loginResponseSchema>;
export type MfaVerificationRequest = z.infer<
  typeof mfaVerificationRequestSchema
>;
export type RefreshTokenRequest = z.infer<typeof refreshTokenRequestSchema>;
export type RefreshTokenResponse = z.infer<typeof refreshTokenResponseSchema>;
export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>;
export type ResetPasswordResponse = z.infer<typeof resetPasswordResponseSchema>;
export type SetupMfaRequest = z.infer<typeof setupMfaRequestSchema>;
export type SetupMfaResponse = z.infer<typeof setupMfaResponseSchema>;
export type ConfirmMfaRequest = z.infer<typeof confirmMfaRequestSchema>;
export type ConfirmMfaResponse = z.infer<typeof confirmMfaResponseSchema>;

export const forgotPasswordRequestSchema = z.object({
  username: z.string().min(1, "Username is required"),
});

export const forgotPasswordResponseSchema = z.object({
  message: z.string().nullable(),
});

export const confirmForgotPasswordRequestSchema = z.object({
  username: z.string().min(1, "Username is required"),
  confirmationCode: z.string().min(1, "Confirmation code is required"),
  newPassword: z.string().min(1, "New password is required"),
});

export const confirmForgotPasswordResponseSchema = z.object({
  message: z.string().nullable(),
});

export type ForgotPasswordRequest = z.infer<typeof forgotPasswordRequestSchema>;
export type ForgotPasswordResponse = z.infer<
  typeof forgotPasswordResponseSchema
>;
export type ConfirmForgotPasswordRequest = z.infer<
  typeof confirmForgotPasswordRequestSchema
>;
export type ConfirmForgotPasswordResponse = z.infer<
  typeof confirmForgotPasswordResponseSchema
>;

export const passwordPolicySchema = z.object({
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
  minAlphabeticChars: z.number().optional(),
  minUppercaseChars: z.number().optional(),
  minLowercaseChars: z.number().optional(),
  minNumericChars: z.number().optional(),
  minSpecialChars: z.number().optional(),
  tempPasswordValidityDays: z.number().optional(),
});

export type PasswordPolicy = z.infer<typeof passwordPolicySchema>;

// ---------------------------------------------------------------------------
// SSO
// ---------------------------------------------------------------------------

export const ssoInitiateResponseSchema = z.object({
  method: z.enum(["SSO", "PASSWORD"]),
  redirectUrl: z.string().optional(),
  displayName: z.string().optional(),
});

export const ssoCallbackRequestSchema = z.object({
  code: z.string().min(1, "code is required"),
  state: z.string().min(1, "state is required"),
});

export const logoutResponseSchema = z.object({
  logoutUrl: z.string().optional(),
});

export type SSOInitiateResponse = z.infer<typeof ssoInitiateResponseSchema>;
export type SSOCallbackRequest = z.infer<typeof ssoCallbackRequestSchema>;
export type LogoutResponse = z.infer<typeof logoutResponseSchema>;
